/**
 * 작성자 : 한영광
 * 날짜 : 2023.07.10
 * 경로 : 센서 관리 - 가스 측정기
 */
import { useEffect, useState, useRef, RefObject } from 'react';
import { toast } from 'react-toastify';
import { PulseLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { IoChevronUpSharp } from 'react-icons/io5';
import { useRecoilValue } from 'recoil';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import styled from 'styled-components';

import { pageInfoState, userState } from '../../atoms';
import { BtnGhost, BtnGray } from '../../components/Button';
import { LIST_COUNT, SENSOR_TIME } from '../../_constants';
import { SearchOptions } from '../../assets/styles/SearchOptions';
import { scrollToNodeTop } from '../../utils/scrollToNodeTop';
import { useDetectScrolledToBottom } from '../../utils/useDetectScrolledToBottom';
import { FloatingButtonToTop } from '../../assets/styles/FloatingButtonToTop';
import Tab from '../../components/Tab';
import { IComCdList, ITabObject } from 'customTypes';
import { setComCdListState } from '../../utils/setComCdListState';
import { todayYYYYMMDD, toMonthYYYMMDD } from '../../utils/formatDate';
import { useReactToPrint } from 'react-to-print';
import Portal from '../../components/Portal';
import RangePicker, { onChangeRangeInput } from '../../components/RangePicker';
import dayjs from 'dayjs';
import LineChartModal from '../../components/Modal/lineChartModal';
import useOnKeydownF9 from '../../utils/useOnKeydownF9';
import { useSetAuth } from '../../utils/useSetAuth';
import { logPost } from '../../services/log';
import TuiGrid from '../../components/Table/TuiGrid';
import SelectBox from '../../components/SelectBox';
import illustrator from '../../assets/images/illustration/31.svg';
import i18n from '../../translation/i18n';
import { LoadingModalBackground } from '../../assets/styles/Modal';
import TuiGridWrapper from '../../components/Table/TuiGridWrapper';
import { useGetFrozenCount } from '../../utils/getFrozenCount';
import BackButton from '../../components/BackButton';
import useSetListCount from '../../utils/useSetListCount';
import nodataIllustrator from '../../assets/images/illustration/304.svg';
import { apiGet } from '../../services/_common';
import ShortcutButton from '../../components/button/ShortcutButton';
import useTuiGridSize from '../../hooks/useTuiGridSize';
import SearchFilterModal from '../../components/Modal/SearchFilterModal';
import { showExcelButton } from '../../utils/deviceAuth';

const Root = styled.div`
  width: 100%;
  &.hideRoot {
    visibility: hidden;
    position: absolute;
  }
  .tui-container {
    flex-grow: 1;
    overflow: hidden;
    margin-left: 0.5rem;
  }
  .emptyData {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    user-select: none;
    padding-bottom: 4rem;
    img {
      width: 16rem;
      /* user-drag: none; */
      -webkit-user-drag: none;
    }
    span {
      color: var(--filled-violet);
    }
  }
  .inputFormsWrapper {
    background-color: var(--board);
    border-radius: 0.5rem;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .searchContent {
    display: flex;
    justify-content: space-between;
    .buttonsWrapper {
      display: flex;
      margin-bottom: 0rem;
    }
  }

  .end {
    justify-content: end;
    > button {
      margin-left: 1rem;
    }
  }

  .thead > .tr {
    > div {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .spaceBetween {
      justify-content: space-evenly;
    }
  }

  .table {
    height: 64rem;
    padding: 0;

    > .tbody > .tr {
      padding: 0.4rem 1rem;
    }
  }

  .tab {
    padding: 0 1rem;
    cursor: pointer;
    height: 3rem;
  }

  .activeTab {
    font-weight: bold;
    border-bottom: 2px solid rgb(63, 66, 84);
  }

  .grid {
    display: grid;
    margin-bottom: 1rem;
    align-items: center;
    min-height: 3.4rem;
  }

  .grid100,
  .grid100T,
  .grid60,
  .grid50,
  .grid30 {
    > div > div {
      display: grid;
      grid-template-columns: 9rem 1fr;
      align-items: center;
    }
  }

  .grid100T {
    grid-template-columns: 9rem 1fr;
  }

  .grid50,
  .grid60 {
    grid-template-columns: 1fr 1fr;
  }

  .grid50 {
    grid-gap: 1rem;
  }

  .grid30 {
    grid-template-columns: 1.5fr 0.7fr 0.8fr;
  }

  .plainText {
    grid-template-columns: 9rem 1fr;

    > :last-child {
      display: block;
    }
  }

  .marginBottomNone {
    margin-bottom: 0;
  }
  .mgas-responsive {
    flex-wrap: nowrap;
    @media (min-width: 1025px) {
      flex-wrap: wrap;
      overflow: visible !important;
    }
  }
  @media screen and (max-width: 767px) {
    .searchContent {
      flex-direction: column;
      > div {
        width: 100% !important;
      }
    }
  }
`;
const Ssensor6 = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const INIT_F_NAME = { type: 'fName', fName: 'A', cdName: t('전체') };
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const { isF9Pressed, setIsF9Pressed } = useOnKeydownF9(); // f9키 프레스 훅
  const { listCount, setListCount, patchUserMenuAPI } = useSetListCount(); // 검색줄수설정 훅
  const tabInfo = useRecoilValue(pageInfoState);
  const toDay = todayYYYYMMDD();
  const toMonth = toMonthYYYMMDD();
  const date = new Date();
  const userInfo = useRecoilValue(userState);
  const { userMenuList } = userInfo;
  const [rowState, setRowState] = useState<any>({});
  const [viewRowState, setViewRowState] = useState<boolean>(false);
  const [tableState, setTableState] = useState<any[]>([]);
  const [tableState2, setTableState2] = useState<any[]>([]);
  const [orgTableState2, setOrgTableState2] = useState<any[]>([]);
  const [columns, setColumns] = useState<any[]>([]);
  const [columns2, setColumns2] = useState<any[]>([]);
  const size = useOutletContext<any>();
  const { defaultMrCd } = useRecoilValue(pageInfoState);
  const [currentTabMrCd, setCurrentTabMrCd] = useState(defaultMrCd);
  const tableRef = useRef<HTMLDivElement>(null);
  const [visibleRangePicker, setVisibleRangePicker] = useState(false);
  const [rangeState, setRangeState] = useState([{ startDate: new Date(date.getFullYear(), date.getMonth(), 1), endDate: new Date(), key: 'selection' }]);
  const [listCountComCdList, setListCountComCdList] = useState<IComCdList[]>([]); // 검색줄수 공통코드
  const [fNameList, setFnameList] = useState<any[]>([]);
  const [searchOption, setSearchOption] = useState({ start: toMonth, end: toDay });
  const [fName, setFName] = useState(INIT_F_NAME);
  const [sensorTime, setSensorTime] = useState<any>({ type: 'time', time: '1', cdName: t('시간') });
  const [excelBtn, setExcelBtn] = useState(false);
  const [excelBtn2, setExcelBtn2] = useState(false);
  const [chartData, setChartData] = useState<any>([]);
  const [chartData2, setChartData2] = useState<any>([]);
  const [datasets] = useState<any>([
    {
      label: t('가스 평균'),
      data: 'avgMq',
      borderColor: 'rgba(0, 221, 255, 0.5)',
      backgroundColor: 'rgba(0, 221, 255, 0.5)',
    },
    {
      label: t('가스 최소'),
      data: 'minMq',
      borderColor: 'rgba(0, 119, 255, 0.5)',
      backgroundColor: 'rgba(0, 119, 255, 0.5)',
    },
    {
      label: t('가스 최대'),
      data: 'maxMq',
      borderColor: 'rgba(0, 0, 255, 0.5)',
      backgroundColor: 'rgba(0, 0, 255, 0.5)',
    },
  ]);
  const [datasets2] = useState<any>([
    {
      label: t('가스'),
      data: 'mq',
      borderColor: 'rgba(0, 221, 255, 0.5)',
      backgroundColor: 'rgba(0, 221, 255, 0.5)',
    },
  ]);
  const { isBottom } = useDetectScrolledToBottom(tableRef);
  const [openModal, setOpenModal] = useState<any>({ status: false, type: '' });
  const [newTabList, setNewTabList] = useState<ITabObject[]>();
  const [loading, setLoading] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);
  const componentRef2 = useRef<HTMLDivElement>(null);
  const { tuiHeight } = useTuiGridSize(componentRef);
  const { tuiHeight: tuiHeight2 } = useTuiGridSize(componentRef2);
  const frozenCount = useGetFrozenCount();
  const showExcelBtn = showExcelButton(auth.excelAuth);

  useEffect(() => {
    const arr: any[] = [];
    userMenuList.map((v: any) => {
      const result = tabInfo?.tabList?.find((el: ITabObject) => el.mtCd === v.mtCd && el.mdCd === v.mdCd && el.mrCd === v.mrCd);
      if (result) {
        arr.push(result);
        setNewTabList(arr);
      }
    });
  }, [tabInfo.tabList]);

  useEffect(() => {
    if (!rangeState[0].startDate) {
      return;
    }
    setSearchOption((prev: any) => ({ ...prev, start: dayjs(rangeState[0].startDate).format('YYYY-MM-DD') }));
  }, [rangeState[0].startDate]);

  useEffect(() => {
    if (!rangeState[0].endDate) {
      return;
    }
    setSearchOption((prev: any) => ({ ...prev, end: dayjs(rangeState[0].endDate).format('YYYY-MM-DD') }));
  }, [rangeState[0].endDate]);

  useEffect(() => {
    setColumns([
      {
        header: t('장비명'),
        name: 'dvName',
        align: 'left',
        sortable: true,
        filter: 'select',
        width: 120,
        renderer: { classNames: ['text_secondary'] },
      },
      {
        header: t('측정일자'),
        name: 'checkTime',
        align: 'center',
        sortable: true,
        // filter: 'select',
        minWidth: 120,
        renderer: { classNames: ['text_secondary'] },
      },
      {
        header: t('가스 평균'),
        name: 'avgMq',
        align: 'center',
        sortable: true,
        filter: 'number',
        minWidth: 120,
      },
      {
        header: t('가스 최소'),
        name: 'minMq',
        align: 'center',
        sortable: true,
        filter: 'number',
        minWidth: 120,
      },
      {
        header: t('가스 최대'),
        name: 'maxMq',
        align: 'center',
        sortable: true,
        filter: 'number',
        minWidth: 120,
      },
    ]);
    setColumns2([
      {
        header: t('장비명'),
        name: 'dvName',
        align: 'left',
        sortable: true,
        width: 120,
        renderer: { classNames: ['text_secondary'] },
      },
      {
        header: t('측정일자/시간'),
        name: 'checkTime',
        align: 'center',
        sortable: true,
        minWidth: 140,
        renderer: { classNames: ['text_secondary'] },
      },
      {
        header: 'PPM',
        name: 'mq',
        align: 'center',
        sortable: true,
        filter: 'number',
        minWidth: 120,
      },
    ]);

    setTableState2(orgTableState2.map((v: any) => ({ ...v, checkTime: `${v.checkTime}${t('시')}` })));
  }, [i18n.language]);

  useEffect(() => {
    setLoading(true);
    getSensorAPI().then((res: any) => {
      if (res.status === 200) {
        getNormalCdAPI();
        setComCdListState(LIST_COUNT, setListCountComCdList, false);
        logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '가스 측정기',
          action: '조회',
          etc: ``,
        });
      }
    });
  }, []);

  useEffect(() => {
    if (isF9Pressed) {
      onClickSearch();
      setIsF9Pressed(false);
    }
  }, [isF9Pressed]);

  useEffect(() => {
    if (Object.keys(rowState).length !== 0) getSensorDayAPI(rowState);
  }, [rowState, sensorTime]);

  // 센서 리스트 조회 API
  const getSensorAPI = async () => {
    const { start, end } = searchOption;
    const req = { hCd: userInfo.hCd, sCd: userInfo.sCd, dvNo: fName.fName === 'A' ? '' : fName.fName, date1: start.replaceAll('-', ''), date2: end.replaceAll('-', '') };
    const res = await apiGet({ path: '/sensor/mq', req });
    const { data, statusCode } = res.data !== undefined && res.data;
    if (statusCode === 200) {
      setLoading(false);
      setTableState(data);
      const dvList = toDvList(data);
      const newData = dvList.map((v: any) => {
        return data.filter((v2: any) => v2.dvNo === v.dvNo);
      });
      const reverseData = newData.map((innerArray: any) => [...innerArray].reverse());
      setChartData(reverseData);
    } else {
      setLoading(false);
      // toast.error(t(ERROR));
    }
    return res;
  };

  // 장비 리스트 조회 API
  const getNormalCdAPI = async () => {
    const req = { hCd: userInfo.hCd, sCd: userInfo.sCd, grCd: '06' };
    const res = await apiGet({ path: '/sensor/detail', req });
    const { data, statusCode } = res.data !== undefined && res.data;
    if (statusCode === 200) {
      const newList = [];
      newList.push(INIT_F_NAME);
      data.sensorList.map((v: any) => {
        newList.push({ type: 'fName', fName: v.subCd, cdName: v.sName });
      });
      setFnameList(newList);
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 센서 일별 조회 API
  const getSensorDayAPI = async (rowData: any) => {
    const req = { hCd: userInfo.hCd, sCd: userInfo.sCd, dvNo: rowData.dvNo, date: rowData.checkTime?.replaceAll('-', ''), time: sensorTime.time };
    const res = await apiGet({ path: '/sensor/mqDay', req });
    const { data, statusCode } = res.data !== undefined && res.data;
    if (statusCode === 200) {
      const reverseData = [data].map((innerArray: any) => [...innerArray].reverse());
      if (sensorTime.time === '1') {
        setOrgTableState2(data);
        setTableState2(data.map((v: any) => ({ ...v, checkTime: `${v.checkTime}${t('시')}` })));
        setChartData2(reverseData);
      } else {
        setTableState2(data);
        setChartData2(reverseData);
      }
    } else {
      // toast.error(t(ERROR));
    }
  };

  const toDvList = (data: any[]) => {
    const map = new Map(data.map((m: any) => [m.dvNo, m])).entries();
    return Array.from(map).map((v: any) => {
      return { dvNo: v[1].dvNo, dvName: v[1].dvName };
    });
  };

  // 검색 버튼 클릭
  const onClickSearch = (filterRef?: RefObject<HTMLDivElement>) => {
    if (!openModal.status || (openModal.status && openModal.type === 'filter')) {
      if (searchOption.end && searchOption.start > searchOption.end) {
        toast.warning(t('날짜를 다시 입력하세요'));
        setSearchOption((prev: any) => ({ ...prev, end: '' }));
      } else {
        setLoading(true);
        if (filterRef) {
          // 모바일일 경우 검색 필터 모달 창 닫기
          filterRef.current?.classList.remove('active');
          setOpenModal({ status: false, type: 'filter' });
        }
        setRowState({});
        setTableState2([]);
        setVisibleRangePicker(false);
        navigate({ pathname: location.pathname, search: '' });
        getSensorAPI();
      }
    }
  };

  // 초기화 버튼 클릭
  const onClickInitiateSearchOption = () => {
    Object.keys(searchOption).map((el: any) => {
      return setSearchOption((prev: any) => ({ ...prev, [el]: '', start: toMonth, end: toDay }));
    });
    setFName(INIT_F_NAME);
    setRangeState([{ startDate: new Date(date.getFullYear(), date.getMonth(), 1), endDate: new Date(), key: 'selection' }]);
  };

  // 차트 버튼 클릭
  const onClickChart = () => {
    if (tableState.length !== 0) setOpenModal({ status: true, type: 'lineChart' });
    else toast.warning(t('데이터가 없습니다'));
  };

  // 차트2 버튼 클릭
  const onClickChart2 = () => {
    if (tableState2.length !== 0) setOpenModal({ status: true, type: 'lineChart2' });
    else toast.warning(t('데이터가 없습니다'));
  };

  // 엑셀 저장 버튼 클릭
  const onClickExcelSave = () => {
    if (tableState.length <= 0) return;
    setExcelBtn(true);
    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: '가스 측정기',
      action: '엑셀 저장',
      etc: `${searchOption.start}~${searchOption.end}`,
    });
  };

  // 엑셀 저장 버튼 클릭
  const onClickExcelSave2 = () => {
    if (tableState2.length <= 0) return;
    setExcelBtn2(true);
    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: '가스 측정기',
      action: '엑셀 저장',
      etc: `${rowState.checkTime}`,
    });
  };

  const handlePrint = useReactToPrint({ content: () => componentRef.current });
  const handlePrint2 = useReactToPrint({ content: () => componentRef2.current });
  // 인쇄 버튼 클릭
  const onClickPrint = () => {
    handlePrint();
  };
  // 인쇄 버튼 클릭
  const onClickPrint2 = () => {
    handlePrint2();
  };

  // 측정기 Row 클릭
  const onClickRow = async (rowData: any) => {
    if (rowData) {
      setRowState(rowData);
      setViewRowState(true);
    }
  };

  const renderExcelBtn = () => {
    if (showExcelBtn) {
      return (
        <BtnGhost onClick={onClickExcelSave}>
          <span className='icon-ms-xlsx'>X</span> {t('엑셀')}
        </BtnGhost>
      );
    }
    return null;
  };

  const renderExcelBtn2 = () => {
    if (showExcelBtn) {
      return (
        <BtnGhost onClick={onClickExcelSave2}>
          <span className='icon-ms-xlsx'>X</span> {t('엑셀')}
        </BtnGhost>
      );
    }
    return null;
  };

  const onClickRangeInput = () => {
    if (!visibleRangePicker) setVisibleRangePicker(true);
  };

  const onClickTab = (tab: string) => {
    setCurrentTabMrCd(tab);
  };

  // 필터 버튼 클릭
  const onClickFilter = () => {
    setOpenModal({ status: true, type: 'filter' });
  };

  return (
    <div className='contents'>
      <Tab tabList={newTabList || tabInfo.tabList} currentTabMrCd={currentTabMrCd} setCurrentTabMrCd={setCurrentTabMrCd} onClickTab={onClickTab} size={size} />
      <div className={`content-container ${size.innerSize.W > 1024 ? 'twoColumn column-55 mediaSet768' : 'oneColumn'}`}>
        <Root className={size.innerSize.W > 1024 || !viewRowState ? 'showRoot' : 'hideRoot'}>
          <SearchOptions>
            {size.innerSize.W > 1024 ? (
              <>
                {isBottom && (
                  <FloatingButtonToTop>
                    <button type='button' onClick={() => scrollToNodeTop(tableRef)}>
                      <IoChevronUpSharp size={20} style={{ stroke: 'white' }} />
                    </button>
                  </FloatingButtonToTop>
                )}
                <div className='inputsWrapper'>
                  <div className='inputForm-col withLabelComCf'>
                    <label htmlFor='fName'>{t('장비')}</label>
                    <SelectBox
                      options={fNameList}
                      defaultOption={t('전체')}
                      state={fName}
                      setState={setFName}
                      stateKey='fName'
                      filterbar='filter-1-left'
                      initiateKey={fName.fName}
                      optionHeight='height-md'
                    />
                  </div>
                  <div className='inputForm-row-fit'>
                    <span className='calendarLabel'>{t('조회일자')}</span>
                    <div className='inputForm-col'>
                      <div className='flex-basic'>
                        <input id='startInput' type='text' value={searchOption.start} onChange={(e) => onChangeRangeInput(e, 'start', setRangeState, setSearchOption)} />
                        <span className='inputDash'> ~ </span>
                        <input id='endInput' type='text' value={searchOption.end} onChange={(e) => onChangeRangeInput(e, 'end', setRangeState, setSearchOption)} />
                      </div>
                      {visibleRangePicker && (
                        <div className='rangePickerWrapper'>
                          <RangePicker state={rangeState} setState={setRangeState} setVisible={setVisibleRangePicker} />
                        </div>
                      )}
                    </div>
                    <button type='button' className='date-picker-btn' onClick={onClickRangeInput}>
                      <span className='material-symbols-rounded'>calendar_month</span>
                    </button>
                  </div>
                </div>
                <div className='inputsWrapper'>
                  <div className='secondSearchOption'>
                    <div className='flex-basic textBtnGroup'>
                      <ShortcutButton icon='search' buttonText='검색' shortcut='F9' onClick={onClickSearch} />
                      <BtnGhost onClick={onClickInitiateSearchOption}>{t('초기화')}</BtnGhost>
                      <div className='searchResult'>
                        {t('총')}
                        <span>{tableState.length}</span>
                        {t('개')}
                      </div>
                      <div className='inputForm-row'>
                        <div className='inputForm-col withLabelComCf'>
                          <label htmlFor='useYn'>{t('보기 설정')}</label>
                          <SelectBox
                            options={listCountComCdList}
                            defaultOption={listCount.cdName}
                            state={listCount}
                            setState={setListCount}
                            stateKey={LIST_COUNT}
                            initiateKey={listCount[LIST_COUNT]}
                            setTableLinesAPI={patchUserMenuAPI}
                            optionHeight='height-sm'
                            rsearch
                          />
                        </div>
                      </div>
                    </div>

                    <div className='flex-basic iconBtnGroup'>
                      <BtnGhost onClick={onClickChart}>
                        <span className='material-symbols-rounded'>monitoring</span>
                        {t('차트')}
                      </BtnGhost>
                      {renderExcelBtn()}
                      {/* {renderPrintBtn()} */}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className='filter-wrapper'>
                <div className='inputsWrapper'>
                  <div className='flex-basic iconBtnGroup'>
                    <BtnGhost onClick={onClickFilter} className='filter'>
                      <span className='material-symbols-rounded'>filter_alt</span>
                      {t('검색 필터')}
                    </BtnGhost>
                    <BtnGhost onClick={onClickChart}>
                      <span className='material-symbols-rounded'>monitoring</span>
                      {t('차트')}
                    </BtnGhost>
                    {renderExcelBtn()}
                  </div>
                </div>
              </div>
            )}
          </SearchOptions>
          {tableState.length > 0 ? (
            <TuiGridWrapper componentRef={componentRef}>
              <TuiGrid
                data={tableState}
                columns={columns}
                perPage={Number(listCount[LIST_COUNT])}
                excelBtn={excelBtn}
                setExcelBtn={setExcelBtn}
                usePagenation
                onClickRow={onClickRow}
                scrollX
                height={tuiHeight}
                frozenCount={frozenCount}
                pageNum={1}
                gridId='grid1'
              />
            </TuiGridWrapper>
          ) : (
            <div className='emptyData'>
              <img src={nodataIllustrator} alt='noData' />
              <span>{t('해당 일자의 데이터가 존재하지 않습니다')}</span>
            </div>
          )}
        </Root>
        {(size.innerSize.W > 1024 || viewRowState) &&
          (viewRowState ? (
            <Root>
              <SearchOptions align='left'>
                {' '}
                <div className='inputsWrapperGroup'>
                  {size.innerSize.W <= 1024 && <BackButton func={() => setViewRowState(false)} />}
                  <div className='inputsWrapper sensor-results'>
                    <div className='sensor-result-header'>
                      <span>{t('측정일')}</span>
                      <div>{rowState.checkTime}</div>
                    </div>
                    <div className='sensor-result-data'>
                      <span>{t('가스')}</span>
                      <div>
                        <span>{t('평균')}</span>
                        {rowState.avgMq}
                      </div>
                      <div>
                        <span>{t('최소')}</span>
                        {rowState.minMq}
                      </div>
                      <div>
                        <span>{t('최대')}</span>
                        {rowState.maxMq}
                      </div>
                    </div>
                  </div>
                </div>
                <div className='inputsWrapper sensorOverflow'>
                  <div className='secondSearchOption mgas-responsive'>
                    <div className='flex-basic shrink-0'>
                      <SelectBox options={SENSOR_TIME} defaultOption={t('시간')} state={sensorTime} setState={setSensorTime} stateKey='time' />
                    </div>
                    <div className='flex-basic iconBtnGroup'>
                      <BtnGhost onClick={onClickChart2}>
                        <span className='material-symbols-rounded'>monitoring</span>
                        {t('차트')}
                      </BtnGhost>
                      {renderExcelBtn2()}
                      {/* {renderPrintBtn2()} */}
                    </div>
                  </div>
                </div>
              </SearchOptions>
              <TuiGridWrapper componentRef={componentRef2}>
                <TuiGrid
                  data={tableState2}
                  columns={columns2}
                  perPage={Number(listCount[LIST_COUNT])}
                  excelBtn={excelBtn2}
                  setExcelBtn={setExcelBtn2}
                  usePagenation
                  height={tuiHeight2}
                  frozenCount={frozenCount}
                  pageNum={1}
                  gridId='grid2'
                />
              </TuiGridWrapper>
            </Root>
          ) : (
            <Root>
              <div className='emptyData'>
                <img src={illustrator} alt='noData' />
                <span>{t('왼쪽 목록에서 센서를 선택해주세요')}</span>
              </div>
            </Root>
          ))}
      </div>
      <Portal openModal={loading}>
        <LoadingModalBackground>
          <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' style={{ width: 'inherit', height: 'inherit' }} />
        </LoadingModalBackground>
      </Portal>
      <Portal openModal={openModal?.status}>
        {openModal.status && openModal.type === 'lineChart' && <LineChartModal setOpenModal={setOpenModal} chartData={chartData} datasets={datasets} title={t('가스 측정기')} />}
        {openModal.status && openModal.type === 'lineChart2' && <LineChartModal setOpenModal={setOpenModal} chartData={chartData2} datasets={datasets2} title={t('가스 측정기')} />}
      </Portal>
      {size.innerSize.W <= 1024 && (
        <Portal openModal>
          <SearchFilterModal openModal={openModal} setOpenModal={setOpenModal} onClickSearch={onClickSearch} onClickReset={onClickInitiateSearchOption}>
            <div className='filter-option-wrapper'>
              <div className='filter-option-col filter-underline filter-option-date'>
                <span className='calendarLabel'>{t('조회일자')}</span>
                <div className='inputForm-col'>
                  <div className='flex-basic' role='presentation' onClick={onClickRangeInput}>
                    <input id='startInput' type='text' readOnly value={searchOption.start} onChange={(e) => onChangeRangeInput(e, 'start', setRangeState, setSearchOption)} />
                    <span className='inputDash'> ~ </span>
                    <input id='endInput' type='text' readOnly value={searchOption.end} onChange={(e) => onChangeRangeInput(e, 'end', setRangeState, setSearchOption)} />
                  </div>
                  {visibleRangePicker && (
                    <div className='rangePickerWrapper'>
                      <RangePicker state={rangeState} setState={setRangeState} setVisible={setVisibleRangePicker} />
                    </div>
                  )}
                </div>
              </div>
              <div className='filter-option-cont'>
                <div className='filter-option-row'>
                  <label htmlFor='useYn'>{t('보기 설정')}</label>
                  <SelectBox
                    options={listCountComCdList}
                    defaultOption={listCount.cdName}
                    state={listCount}
                    setState={setListCount}
                    stateKey={LIST_COUNT}
                    initiateKey={listCount[LIST_COUNT]}
                    setTableLinesAPI={patchUserMenuAPI}
                    optionHeight='height-sm'
                    rsearch
                  />
                </div>
                <div className='filter-option-row'>
                  <label htmlFor='fName'>{t('장비')}</label>
                  <SelectBox
                    options={fNameList}
                    defaultOption={t('전체')}
                    state={fName}
                    setState={setFName}
                    stateKey='fName'
                    filterbar='filter-1-left'
                    initiateKey={fName.fName}
                    optionHeight='height-md'
                  />
                </div>
              </div>
            </div>
          </SearchFilterModal>
        </Portal>
      )}
    </div>
  );
};

export default Ssensor6;
